import React from 'react';
import { Alert, Button, Segmented, Select, Typography } from 'antd';

import {
  SYNTHETIC_BOND_CURVE_TENOR_MONTHS,
  SyntheticBondGenerationDraft,
  SyntheticBondGenerationOptions,
  SyntheticBondReferenceCurvesResponse,
  SyntheticBondCurveTenorMonths,
} from 'state';
import { BondChartMode } from '../BondsPlayground/BondChart';
import { InfoPoint } from '../BondsPlayground/ModelGuide';
import SystemCurveSlot from './SystemCurveSlot';

type Props = {
  data?: SyntheticBondReferenceCurvesResponse;
  disabled?: boolean;
  draft: SyntheticBondGenerationDraft;
  error?: string;
  fetching?: boolean;
  mode: BondChartMode;
  options?: SyntheticBondGenerationOptions;
  onCurrencyChange: (currencyId: number | null) => void;
  onModeChange: (mode: BondChartMode) => void;
  onRetry: () => void;
  onSelectSource: (
    tenorMonths: SyntheticBondCurveTenorMonths,
    assetId: number | null,
  ) => void;
};

const SystemCurveSidecar: React.FC<Props> = ({
  data,
  disabled,
  draft,
  error,
  fetching,
  mode,
  onCurrencyChange,
  onModeChange,
  onRetry,
  onSelectSource,
  options,
}) => {
  const slots = new Map(
    (data?.tenors || []).map(slot => [Number(slot.tenor_months), slot]),
  );

  return (
    <aside className="bonds-playground__sidecar synthetic-bonds__sidecar">
      <div className="synthetic-bonds__sidecar-header">
        <div>
          <div className="bonds-playground__sidecar-title">
            <Typography.Title level={5}>System yield sources</Typography.Title>
            <InfoPoint title="Exact source map">
              Each card assigns one exact UnderlyingAsset to a maintained tenor.
              The selected currency is shared with the family form. Asset names
              never determine tenor automatically.
            </InfoPoint>
          </div>
          <Typography.Text type="secondary">
            Search, assign, and inspect all seven nodes.
          </Typography.Text>
        </div>
        <Segmented
          options={[
            { label: 'Prices', value: 'price' },
            { label: 'Yields', value: 'yield' },
          ]}
          value={mode}
          onChange={value => onModeChange(value as BondChartMode)}
        />
      </div>

      <label className="synthetic-bonds__sidecar-currency">
        <Typography.Text strong>System curve currency</Typography.Text>
        <Select<number>
          allowClear
          disabled={disabled}
          options={(options?.currencies || []).map(currency => ({
            value: currency.id,
            label: currency.name
              ? `${currency.symbol} · ${currency.name}`
              : currency.symbol,
          }))}
          placeholder="Select family currency"
          showSearch
          optionFilterProp="label"
          value={draft.currency_id ?? undefined}
          onChange={value => onCurrencyChange(value ?? null)}
        />
        <Typography.Text type="secondary">
          This is the same family currency controlled by the main form.
        </Typography.Text>
      </label>

      {error && (
        <Alert
          action={
            <Button size="small" onClick={onRetry}>
              Retry
            </Button>
          }
          className="synthetic-bonds__sidecar-error"
          description={error}
          message="System curves could not be loaded"
          showIcon
          type="error"
        />
      )}

      <div className="synthetic-bonds__curve-slots">
        {SYNTHETIC_BOND_CURVE_TENOR_MONTHS.map(tenorMonths => (
          <SystemCurveSlot
            currencyId={draft.currency_id}
            disabled={disabled}
            key={tenorMonths}
            loading={fetching}
            mode={mode}
            onSelect={assetId => onSelectSource(tenorMonths, assetId)}
            selectedAssetId={
              draft.source_asset_ids_by_tenor_months[String(tenorMonths)]
            }
            slot={slots.get(tenorMonths)}
            tenorMonths={tenorMonths}
          />
        ))}
      </div>
    </aside>
  );
};

export default SystemCurveSidecar;
